import { useLazyQuery } from "apollo/hooks/queries/utils";
import { useEnvContext } from "context/EnvContext";
import { GET_LEGAL_DOCUMENTS } from "core/apollo/graphql";
import { APP_CAREPROVIDER, APP_CLINIC } from "core/consts";
import {
  getObjectLocalStorage,
  setObjectLocalStorage,
} from "core/model/localStorage";
import { getErrorMessage } from "core/model/utils/errors";
import { objectToQueryParamString } from "core/model/utils/strings";
import { LegalDocuments, QueryProgress } from "core/types";
import { addDays, getUnixTime } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector, useLoggedCareproviderId } from "reduxentities/hooks";
import { selectLoggedCareseeker } from "reduxentities/selectors";

type LegalDocumentsLocalStorageType = Record<
  string,
  { expiry: number; value: LegalDocuments }
>;

// Limits to prevent unbounded localStorage growth
const MAX_CACHED_QUERIES = 10;
const MAX_STORAGE_SIZE_BYTES = 512 * 1024; // 512KB soft limit

export type LegalDocumentPropsType =
  | {
      careproviderId?: number;
      careseekerId?: number;
    }
  | undefined;

export type LegalDocumentsContextType = {
  getLegalDocs: (props?: LegalDocumentPropsType) => LegalDocuments | undefined;
  progress: QueryProgress;
  setLegalDocs: (props?: LegalDocumentPropsType) => void;
};

const DEFAULT_QUERY_STRING = "?default";

export const getLegalDocumentQueryString = (props?: LegalDocumentPropsType) => {
  let query = "";

  if (props?.careproviderId) {
    query = objectToQueryParamString({
      careprovider_id: props?.careproviderId,
    });
  }
  if (props?.careseekerId) {
    query = objectToQueryParamString({
      careseeker_id: props?.careseekerId,
    });
  }

  return query || DEFAULT_QUERY_STRING;
};

export const useGetLegalDocumentDefaultProps = (): LegalDocumentPropsType => {
  const careproviderId = useLoggedCareproviderId();
  const careseeker = useAppSelector(selectLoggedCareseeker);
  const { app } = useEnvContext();

  return useMemo(() => {
    if (app === APP_CAREPROVIDER && careproviderId) {
      return {
        careproviderId,
      };
    }
    if (app === APP_CLINIC && careseeker) {
      return {
        careseekerId: careseeker.id,
      };
    }

    return undefined;
  }, [app, careproviderId, careseeker]);
};

const useGetLegalDocsLocalStorageKey = () => {
  const { app } = useEnvContext();
  return `${app}-legal-documents`;
};

/**
 * Cleanup expired entries from legal documents localStorage
 * Returns cleaned data with expired entries removed
 */
const cleanupExpiredEntries = (
  data: LegalDocumentsLocalStorageType | undefined,
): LegalDocumentsLocalStorageType | undefined => {
  if (!data) return undefined;

  const now = getUnixTime(new Date());
  const cleaned: LegalDocumentsLocalStorageType = {};
  let hasExpired = false;

  for (const [key, entry] of Object.entries(data)) {
    if (entry.expiry > now) {
      cleaned[key] = entry;
    } else {
      hasExpired = true;
    }
  }

  return hasExpired ? cleaned : data;
};

/**
 * Enforce size and count limits on legal documents cache
 * Removes oldest entries if limits are exceeded
 */
const enforceLimits = (
  data: LegalDocumentsLocalStorageType,
  newQuery: string,
): LegalDocumentsLocalStorageType => {
  const entries = Object.entries(data);

  // Check count limit
  if (entries.length > MAX_CACHED_QUERIES) {
    // Sort by expiry (oldest first) and keep newest MAX_CACHED_QUERIES
    const sorted = entries.sort(([, a], [, b]) => a.expiry - b.expiry);
    const toKeep = sorted.slice(-MAX_CACHED_QUERIES);
    const limited = Object.fromEntries(toKeep);

    console.info(
      `Cleaned up ${
        entries.length - toKeep.length
      } old legal document cache entries`,
    );

    return limited;
  }

  // Check size limit (estimate)
  const estimatedSize = JSON.stringify(data).length * 2; // UTF-16 chars = 2 bytes
  if (estimatedSize > MAX_STORAGE_SIZE_BYTES) {
    // Remove oldest entries until under limit
    const sorted = entries.sort(([, a], [, b]) => a.expiry - b.expiry);
    const kept: Array<[string, { expiry: number; value: LegalDocuments }]> = [];
    let currentSize = 0;

    // Keep newest entries, ensuring we keep the new query
    for (let i = sorted.length - 1; i >= 0; i--) {
      const [key, entry] = sorted[i];
      const entrySize = JSON.stringify({ [key]: entry }).length * 2;

      if (
        currentSize + entrySize <= MAX_STORAGE_SIZE_BYTES ||
        key === newQuery
      ) {
        kept.unshift(sorted[i]);
        currentSize += entrySize;
      }
    }

    console.info(
      `Cleaned up ${
        entries.length - kept.length
      } legal document cache entries due to size limit`,
    );

    return Object.fromEntries(kept);
  }

  return data;
};

export function useGetLegalDocumentsContext(): LegalDocumentsContextType {
  const localStorageKey = useGetLegalDocsLocalStorageKey();
  const defaultProps = useGetLegalDocumentDefaultProps();

  const [getLegalDocuments, progress] = useLazyQuery<{
    legalDocuments: {
      legal_document_map: LegalDocuments;
    };
  }>(GET_LEGAL_DOCUMENTS, {
    fetchPolicy: "network-only",
  });

  const [docs, setDocs] = useState(() => {
    const stored =
      getObjectLocalStorage<LegalDocumentsLocalStorageType>(localStorageKey);
    return cleanupExpiredEntries(stored);
  });

  const setLegalDocs = useCallback(
    (props?: LegalDocumentPropsType) => {
      const expiry = getUnixTime(addDays(new Date(), 1));
      const queryKey = getLegalDocumentQueryString(props);

      getLegalDocuments({ params: queryKey })
        .then(({ data }) => {
          const result = data?.legalDocuments?.legal_document_map;
          if (!result) throw new Error("No data returned");
          let value: LegalDocumentsLocalStorageType = {
            [queryKey]: { expiry, value: result },
          };

          // if the current query is not empty
          // we can append the results with other careseeker / careprovider ids
          // for those with roles for multiple
          if (queryKey !== DEFAULT_QUERY_STRING) {
            // Clean up expired entries before merging
            const cleanedDocs = cleanupExpiredEntries(docs);

            value = {
              ...(cleanedDocs ?? {}),
              ...value,
            };

            // Enforce limits to prevent unbounded growth
            value = enforceLimits(value, queryKey);
          }

          setObjectLocalStorage(localStorageKey, value);
          setDocs(value);
        })
        .catch((err) => {
          console.error(
            `error fetching legal documents - ${getErrorMessage(err)}`,
          );
        });
    },
    [getLegalDocuments, docs, localStorageKey],
  );

  // The logged-out cache normalization must run at most once per defaultProps
  // value (mirroring the pre-POW-482 effect that only depended on
  // defaultProps). Re-running it on every docs change would prune the
  // careprovider/careseeker entries added via setLegalDocs during logged-out
  // flows such as careprovider onboarding, hiding the DPA/terms section.
  const hasNormalizedLoggedOutCacheRef = useRef(false);

  useEffect(() => {
    hasNormalizedLoggedOutCacheRef.current = false;
  }, [defaultProps]);

  useEffect(() => {
    const date = getUnixTime(new Date());
    const query = getLegalDocumentQueryString(defaultProps);
    const storedResult = docs?.[query];

    if (
      storedResult?.value &&
      storedResult?.expiry &&
      storedResult.expiry > date
    ) {
      // if the query is empty, it means we are logged out
      // or in a state where we should store the legal docs without multiple queries
      // i.e. the provider search apps
      if (query === DEFAULT_QUERY_STRING) {
        const hasExtraEntries =
          !hasNormalizedLoggedOutCacheRef.current &&
          docs !== undefined &&
          Object.keys(docs).some((key) => key !== query);
        hasNormalizedLoggedOutCacheRef.current = true;
        if (hasExtraEntries) {
          const loggedOutValue = {
            [query]: storedResult,
          };
          setObjectLocalStorage(localStorageKey, loggedOutValue);
          setDocs(loggedOutValue);
        }
      } else {
        // Clean up expired entries even when we have valid cache
        const cleaned = cleanupExpiredEntries(docs);
        if (cleaned !== docs) {
          setObjectLocalStorage(localStorageKey, cleaned ?? {});
          setDocs(cleaned);
        }
      }

      return;
    }
    // if we are missing the documents for a specific query, or they have expired
    // fetch them and store in localStorage
    setLegalDocs(defaultProps);
  }, [defaultProps, docs, localStorageKey, setLegalDocs]);

  return {
    getLegalDocs: (props?: {
      careproviderId?: number;
      careseekerId?: number;
    }) => {
      const queryKey = getLegalDocumentQueryString(props ?? defaultProps);
      return docs?.[queryKey]?.value;
    },
    progress,
    setLegalDocs,
  };
}
